import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import Button from "../../components/ui/Button";
import PaymentStatusLayout from "./PaymentStatusLayout";
import PaymentPageLogo, { PaymentEyebrow } from "./PaymentPageBrandHeader";
import PaymentSuccessIcon from "./PaymentSuccessIcon";
import { PAYMENT_URL } from "../../constants";
import { refreshBillingSession } from "../../components/Billings/billingSession";
import { getStoredOwner, getStoredUser } from "../../utils/authStorage";

type CheckoutSessionSummary = {
  planLabel?: string;
  plan?: string;
  interval?: string;
  customerEmail?: string;
  status?: string;
  paid?: boolean;
  mode?: string;
  trial?: boolean;
};

function formatIntervalLabel(interval?: string) {
  const value = String(interval || "").toLowerCase();
  if (value === "yearly") return "Yearly billing";
  if (value === "quarterly") return "Quarterly billing";
  if (value === "monthly") return "Monthly billing";
  return "";
}

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [sessionSummary, setSessionSummary] = useState<CheckoutSessionSummary | null>(null);
  const [isConfirming, setIsConfirming] = useState(true);
  const billingRefreshStartedRef = useRef(false);

  const isAuthenticated = Boolean(getStoredUser()?.userId);

  // Safepay sometimes builds: .../success?session_id=chk_xxx?tracker=track_yyy
  // (second "?" instead of "&"), which puts tracker inside session_id.
  const rawSessionId = String(searchParams.get("session_id") || "").trim();
  const mangledTracker =
    rawSessionId.includes("?") && rawSessionId.includes("tracker=")
      ? String(
          new URLSearchParams(rawSessionId.split("?").slice(1).join("?")).get(
            "tracker"
          ) || ""
        ).trim()
      : "";
  const sessionId = rawSessionId.split("?")[0].trim();
  const trackerFromUrl = String(
    searchParams.get("tracker") ||
      searchParams.get("tracker_token") ||
      mangledTracker ||
      ""
  ).trim();

  useEffect(() => {
    if (!sessionId && !trackerFromUrl) {
      setIsConfirming(false);
      return;
    }

    let disposed = false;
    let attempts = 0;
    const maxAttempts = 12;
    const idsToTry = Array.from(
      new Set([sessionId, trackerFromUrl].filter(Boolean))
    );

    const poll = async () => {
      attempts += 1;
      try {
        let data: CheckoutSessionSummary | null = null;
        for (const id of idsToTry) {
          const resp = await fetch(
            `${PAYMENT_URL}/checkoutSession/${encodeURIComponent(id)}`
          );
          if (!resp.ok) continue;
          const json = await resp.json();
          if (json && typeof json === "object") {
            data = json as CheckoutSessionSummary;
            if (
              Boolean(data.paid) ||
              String(data.status || "").toLowerCase() === "completed"
            ) {
              break;
            }
          }
        }
        if (disposed) return;
        if (data) {
          setSessionSummary(data);
          const paid =
            Boolean(data.paid) || String(data.status || "").toLowerCase() === "completed";
          if (paid || attempts >= maxAttempts) {
            setIsConfirming(false);
            return;
          }
        } else if (attempts >= maxAttempts) {
          setIsConfirming(false);
          return;
        }
      } catch (_) {
        if (disposed) return;
        if (attempts >= maxAttempts) {
          setIsConfirming(false);
          return;
        }
      }
      window.setTimeout(poll, 1500);
    };

    void poll();
    return () => {
      disposed = true;
    };
  }, [sessionId, trackerFromUrl]);

  useEffect(() => {
    if (!isAuthenticated || isConfirming || billingRefreshStartedRef.current) return;
    billingRefreshStartedRef.current = true;
    refreshBillingSession({
      owner: getStoredOwner(),
      currentUser: getStoredUser(),
      dispatch,
    }).catch(() => {});
  }, [dispatch, isAuthenticated, isConfirming]);

  const planName = useMemo(() => {
    return String(sessionSummary?.planLabel || "").trim();
  }, [sessionSummary]);

  const intervalLabel = useMemo(() => {
    return formatIntervalLabel(sessionSummary?.interval);
  }, [sessionSummary]);

  const isPaid =
    Boolean(sessionSummary?.paid) ||
    String(sessionSummary?.status || "").toLowerCase() === "completed";
  const isTrial = Boolean(sessionSummary?.trial) || sessionSummary?.mode === "trial";

  return (
    <PaymentStatusLayout>
      <PaymentPageLogo />

      <div className="mt-10">
        <PaymentSuccessIcon />
        <div className="mt-8">
          <PaymentEyebrow>
            {isConfirming
              ? isTrial
                ? "Activating trial"
                : "Confirming payment"
              : isTrial
                ? "Trial started"
                : isPaid
                  ? "Payment successful"
                  : "Payment received"}
          </PaymentEyebrow>
        </div>
        <h1 className="mt-3 text-[1.75rem] font-semibold leading-snug text-dark-black">
          {isConfirming
            ? isTrial
              ? "Activating your free trial…"
              : "Confirming your payment…"
            : isPaid
              ? isTrial
                ? "Your free trial is active"
                : "Thank you for your purchase"
              : "We're still confirming your payment"}
        </h1>
        {isConfirming ? (
          <p className="mt-4 text-sm leading-6 text-dark-grey">
            {isTrial
              ? "We're confirming your card on file. You won't be charged until the trial ends."
              : "We're verifying your payment with our processor. This usually takes a few seconds."}
          </p>
        ) : isPaid && planName ? (
          <div className="mt-5 flex flex-col items-center gap-1">
            <span className="inline-flex items-center rounded-full bg-success-bg px-4 py-1.5 text-sm font-medium text-success">
              {planName}
            </span>
            {intervalLabel ? (
              <p className="text-xs text-dark-grey">{intervalLabel}</p>
            ) : null}
          </div>
        ) : isPaid ? (
          <p className="mt-4 text-sm leading-6 text-dark-grey">
            {isTrial
              ? "Your trial has been confirmed. You won't be charged until it ends."
              : "Your subscription has been confirmed."}
          </p>
        ) : (
          <p className="mt-4 text-sm leading-6 text-dark-grey">
            Card verification finished on Safepay, but your plan is not active yet.
            Open Billing or refresh this page in a few seconds. If it stays empty,
            contact support with your checkout reference.
          </p>
        )}
      </div>

      {!isConfirming ? (
        <div className="mt-8 rounded-2xl bg-bg-light-blue/80 px-6 py-5 text-left">
          {isAuthenticated && isPaid ? (
            <>
              <p className="text-sm font-medium text-dark-black">You&apos;re ready to go</p>
              <p className="mt-2.5 text-sm leading-6 text-dark-grey">
                {isTrial
                  ? "Your trial plan is active. Return to your dashboard to connect senders and launch campaigns. Billing starts after the trial if you keep the plan."
                  : "Your subscription is active. Return to your dashboard to connect senders and launch campaigns."}
              </p>
            </>
          ) : isAuthenticated && !isPaid ? (
            <>
              <p className="text-sm font-medium text-dark-black">Almost done</p>
              <p className="mt-2.5 text-sm leading-6 text-dark-grey">
                Check Billing in a moment. If your plan is still inactive, try Update payment
                method &amp; pay, or contact support with your checkout reference.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-dark-black">Almost there</p>
              <p className="mt-2.5 text-sm leading-6 text-dark-grey">
                Sign in to start using your plan.
              </p>
            </>
          )}
        </div>
      ) : null}

      {isAuthenticated ? (
        <div className="mt-10 flex flex-col gap-3.5">
          {isPaid ? (
            <Button
              variant="primary"
              fullWidth
              size="sm"
              isDisabled={isConfirming}
              onClick={() => navigate("/", { replace: true })}
            >
              Go to dashboard
            </Button>
          ) : null}
          <Button
            variant={isPaid ? "outline" : "primary"}
            fullWidth
            size="sm"
            isDisabled={isConfirming}
            onClick={() => navigate("/settings/billing", { replace: true })}
          >
            {isPaid ? "View billing" : "Go to billing"}
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-10">
            <Button
              variant="primary"
              fullWidth
              size="sm"
              isDisabled={isConfirming}
              onClick={() => navigate("/login", { replace: true })}
            >
              Sign in
            </Button>
          </div>
          <p className="mt-6 text-sm leading-6 text-dark-grey">
            Need an account?{" "}
            <Link to="/register" className="font-medium text-brand-blue underline underline-offset-2">
              Create one
            </Link>
          </p>
        </>
      )}
    </PaymentStatusLayout>
  );
}
